import { useEffect, useMemo, useRef, useState } from "react";
import { api, ApiError } from "./api";
import { Button, Empty, Field, Icon, Input, Notice, Spinner, Textarea } from "./components";
import type { Bootstrap, Product } from "./types";
import { clientUuid, dateLabel, sourceFromLocation, yen } from "./utils";

type Receipt = {
  id: string;
  order_number: string;
  pickup_date: string;
  start_time: string;
  end_time: string;
  customer_name: string;
  estimated_total_yen: number;
  items: Array<{ product_name: string; unit_price_yen: number; quantity: number }>;
};

const STEPS = ["受け取り日時", "商品を選ぶ", "お客様情報", "内容確認"];
const DRAFT_KEY = "komugiya-order-draft-v1";

type OrderDraft = { step: number; pickupDate: string; windowId: string; quantities: Record<string, number>; name: string; phone: string; note: string };

function loadDraft(): Partial<OrderDraft> {
  try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY) ?? "{}"); } catch { return {}; }
}

function productAvailable(product: Product, pickupDate: string, today: string): boolean {
  if (product.status !== "active") return false;
  const pickupWeekday = new Date(`${pickupDate}T12:00:00Z`).getUTCDay();
  if (product.available_weekdays?.length && !product.available_weekdays.includes(pickupWeekday)) return false;
  const target = new Date(`${pickupDate}T00:00:00+09:00`).getTime();
  const base = new Date(`${today}T00:00:00+09:00`).getTime();
  return Math.floor((target - base) / 86400000) >= product.minimum_lead_days;
}

export default function OrderApp() {
  const initialDraft = useMemo(loadDraft, []);
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [loadError, setLoadError] = useState("");
  const [step, setStep] = useState(Math.min(3, Math.max(0, Number(initialDraft.step ?? 0))));
  const [pickupDate, setPickupDate] = useState(initialDraft.pickupDate ?? "");
  const [windowId, setWindowId] = useState(initialDraft.windowId ?? "");
  const [quantities, setQuantities] = useState<Record<string, number>>(initialDraft.quantities ?? {});
  const [name, setName] = useState(initialDraft.name ?? "");
  const [phone, setPhone] = useState(initialDraft.phone ?? "");
  const [note, setNote] = useState(initialDraft.note ?? "");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [idempotencyKey, setIdempotencyKey] = useState(clientUuid);
  const [daysExpanded, setDaysExpanded] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState("");
  const timeSectionRef = useRef<HTMLDivElement>(null);

  const load = async () => {
    setLoadError("");
    try {
      const data = await api<Bootstrap>("/api/public/bootstrap");
      setBootstrap(data);
      const first = data.days.find((day) => !day.closed && day.windows.some((window) => window.available));
      if (first) setPickupDate((current) => current || first.date);
    } catch (requestError) {
      setLoadError(requestError instanceof Error ? requestError.message : "予約情報を読み込めませんでした。");
    }
  };

  useEffect(() => { void load(); }, []);
  useEffect(() => {
    if (receipt) return;
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ step, pickupDate, windowId, quantities, name, phone, note } satisfies OrderDraft));
  }, [step, pickupDate, windowId, quantities, name, phone, note, receipt]);

  const day = bootstrap?.days.find((candidate) => candidate.date === pickupDate);
  const selectedWindow = day?.windows.find((window) => window.id === windowId);
  const selectedItems = useMemo(() => bootstrap?.products
    .filter((product) => (quantities[product.id] ?? 0) > 0)
    .map((product) => ({ product, quantity: quantities[product.id] })) ?? [], [bootstrap, quantities]);
  const itemCount = selectedItems.reduce((sum, item) => sum + item.quantity, 0);
  const total = selectedItems.reduce((sum, item) => sum + item.product.price_yen * item.quantity, 0);

  const selectDate = (date: string) => {
    setPickupDate(date);
    setWindowId("");
    setError("");
    window.setTimeout(() => timeSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
  };

  const changeQuantity = (product: Product, delta: number) => {
    setQuantities((current) => {
      const next = Math.max(0, Math.min(product.max_quantity_per_order, (current[product.id] ?? 0) + delta));
      return { ...current, [product.id]: next };
    });
  };

  const next = () => {
    setError("");
    if (step === 0 && (!pickupDate || !windowId)) return setError("受け取り日時を選んでください。");
    if (step === 1 && itemCount === 0) return setError("商品を1点以上選んでください。");
    if (step === 2) {
      if (!name.trim()) return setError("お名前を入力してください。");
      if (!/^(0[0-9ー―‐\-\s]{9,14}|\+81[0-9ー―‐\-\s]{9,14})$/.test(phone.trim())) return setError("電話番号を正しく入力してください。");
    }
    setStep((current) => Math.min(3, current + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (allowDuplicate = false) => {
    setSubmitting(true);
    setError("");
    try {
      const result = await api<{ order: Receipt }>("/api/public/orders", {
        method: "POST",
        headers: { "idempotency-key": idempotencyKey },
        body: JSON.stringify({
          pickup_date: pickupDate,
          pickup_window_id: windowId,
          customer_name: name,
          customer_phone: phone,
          customer_note: note,
          source: sourceFromLocation(),
          source_detail: window.location.href.slice(0, 200),
          items: selectedItems.map(({ product, quantity }) => ({ product_id: product.id, quantity })),
          allow_duplicate: allowDuplicate,
        }),
      });
      setReceipt(result.order);
      sessionStorage.removeItem(DRAFT_KEY);
      setDuplicateWarning("");
      if ("setAppBadge" in navigator) void navigator.setAppBadge?.(0);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (requestError) {
      if (requestError instanceof ApiError && requestError.code === "suspected_duplicate") setDuplicateWarning(requestError.message);
      else setError(requestError instanceof ApiError ? requestError.message : "予約を送信できませんでした。");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setStep(0); setPickupDate(""); setWindowId(""); setQuantities({}); setName(""); setPhone(""); setNote(""); setReceipt(null);
    sessionStorage.removeItem(DRAFT_KEY); setDuplicateWarning(""); setDaysExpanded(false); setIdempotencyKey(clientUuid()); void load();
  };

  const shareReceipt = async () => {
    if (!receipt) return;
    const text = `komugiya ナカシマ\n予約番号：${receipt.order_number}\n受け取り：${dateLabel(receipt.pickup_date, true)} ${receipt.start_time}–${receipt.end_time}`;
    if (navigator.share) await navigator.share({ title: "komugiya ご予約", text }).catch(() => undefined);
    else { await navigator.clipboard?.writeText(text); window.alert("予約内容をコピーしました。"); }
  };

  if (loadError) return <main id="main-content" className="order-shell"><Empty title="予約情報を読み込めませんでした" description={loadError} action={<Button onClick={() => void load()}>もう一度試す</Button>} /></main>;
  if (!bootstrap) return <main id="main-content" className="order-shell"><Spinner label="予約できる商品を確認しています" /></main>;

  if (receipt) return <main id="main-content" className="order-shell order-shell--complete">
    <section className="receipt">
      <div className="receipt__mark" aria-hidden="true">✓</div>
      <p className="eyebrow">ご予約完了</p>
      <h1>ご予約を受け付けました</h1>
      <p className="receipt__intro">当日はお名前をスタッフにお伝えください。</p>
      <div className="receipt__ticket">
        <span className="ticket-label">予約番号</span><strong>{receipt.order_number}</strong>
        <dl>
          <div><dt>受け取り</dt><dd>{dateLabel(receipt.pickup_date, true)}<br/>{receipt.start_time}–{receipt.end_time}</dd></div>
          <div><dt>お名前</dt><dd>{receipt.customer_name} 様</dd></div>
          <div><dt>商品</dt><dd>{receipt.items.map((item) => <span key={item.product_name}>{item.product_name} × {item.quantity}<br/></span>)}</dd></div>
          <div><dt>お支払い予定</dt><dd>{yen(receipt.estimated_total_yen)}</dd></div>
        </dl>
      </div>
      <Notice>お支払いは店頭でお願いいたします。変更・キャンセルは店舗へご連絡ください。</Notice>
      <div className="receipt__actions"><Button onClick={() => void shareReceipt()}>予約内容を共有</Button><Button variant="secondary" onClick={reset}>別の予約をする</Button></div>
    </section>
  </main>;

  return <main id="main-content" className="order-shell">
    <header className="order-header">
      <a href="/order" className="brand brand--customer"><img className="brand__logo" src="/komugiya-logo.png" alt="komugiya ナカシマ"/><span className="brand__shop">大道本店</span></a>
      <div className="step-meter" aria-label={`ステップ ${step + 1} / 4`}><span style={{ width: `${((step + 1) / 4) * 100}%` }}/></div>
      <p className="step-kicker">{step + 1} / 4</p>
      <h1>{STEPS[step]}</h1>
    </header>

    {step === 0 ? <section className="order-section">
      <div className="section-heading"><h2>受け取り日</h2><p>営業日の中からお選びください</p></div>
      <div className="date-strip" role="group" aria-label="受け取り日を選択">
        {bootstrap.days.slice(0, daysExpanded ? 14 : 7).map((candidate) => <button type="button" key={candidate.date}
          className={`date-card ${pickupDate === candidate.date ? "is-selected" : ""}`}
          disabled={candidate.closed || !candidate.windows.some((window) => window.available)} onClick={() => selectDate(candidate.date)}>
          <span>{dateLabel(candidate.date)}</span><small>{candidate.closed ? candidate.note || "お休み" : candidate.windows.some((window) => window.available) ? "受付中" : "受付終了"}</small>
        </button>)}
      </div>
      {!daysExpanded ? <button className="show-more-dates" type="button" onClick={() => setDaysExpanded(true)}>さらに日付を表示</button> : null}
      {pickupDate ? <div ref={timeSectionRef} className="time-section"><div className="section-heading section-heading--spaced"><h2>受け取り時間</h2><p>1時間の受け取り枠です</p></div>
        <div className="time-grid">
          {day?.windows.map((window) => <button type="button" key={window.id} disabled={!window.available}
            className={`time-card ${windowId === window.id ? "is-selected" : ""}`} onClick={() => { setWindowId(window.id); setError(""); }}>
            <strong>{window.start_time}–{window.end_time}</strong>
            <small>{window.remaining == null ? "受付中" : window.remaining > 2 ? "受付中" : window.remaining > 0 ? `残り${window.remaining}枠` : "受付終了"}</small>
          </button>)}
        </div></div> : null}
    </section> : null}

    {step === 1 ? <section className="order-section product-list">
      {bootstrap.products.filter((product) => product.status !== "archived").map((product) => {
        const available = productAvailable(product, pickupDate, bootstrap.today);
        const quantity = quantities[product.id] ?? 0;
        return <article className={`product-card ${!available ? "is-unavailable" : ""}`} key={product.id}>
          <div className="product-card__image">{product.image_url ? <img src={product.image_url} alt={product.name}/> : <span className="product-placeholder" aria-hidden="true"/>}</div>
          <div className="product-card__copy"><div><h2>{product.name}</h2><p>{product.description}</p></div>
            <div className="product-card__bottom"><strong>{yen(product.price_yen)}</strong>
              {available ? <div className="stepper"><button type="button" onClick={() => changeQuantity(product, -1)} disabled={!quantity} aria-label={`${product.name}を1個減らす`}>−</button><output aria-live="polite">{quantity}</output><button type="button" onClick={() => changeQuantity(product, 1)} disabled={quantity >= product.max_quantity_per_order} aria-label={`${product.name}を1個増やす`}>＋</button></div>
                : <span className="sold-out">{product.status === "sold_out" ? "売り切れ" : product.available_weekdays?.length && !product.available_weekdays.includes(new Date(`${pickupDate}T12:00:00Z`).getUTCDay()) ? "この日は予約不可" : `${product.minimum_lead_days}日前まで`}</span>}
            </div>
          </div>
        </article>;
      })}
    </section> : null}

    {step === 2 ? <section className="order-section form-stack">
      <Field label="お名前" hint="受け取り時に確認するお名前です"><Input autoFocus autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} placeholder="例）中島 花子" required /></Field>
      <Field label="電話番号" hint="予約内容の確認が必要な場合のみ使用します"><Input type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="例）090-1234-5678" required /></Field>
      <Field label="備考（任意）" hint="数字のパン、ご要望、アレルギーに関する確認など"><Textarea value={note} onChange={(event) => setNote(event.target.value)} rows={5} placeholder="店舗へ伝えたいことがあればご記入ください" maxLength={500}/></Field>
      <p className="privacy-copy">入力いただいた情報は、ご予約商品の準備と必要なご連絡のためにのみ使用します。</p>
    </section> : null}

    {step === 3 ? <section className="order-section review">
      <ReviewBlock title="受け取り日時" onEdit={() => setStep(0)}><strong>{dateLabel(pickupDate, true)}</strong><span>{selectedWindow?.start_time}–{selectedWindow?.end_time}</span></ReviewBlock>
      <ReviewBlock title="商品" onEdit={() => setStep(1)}>{selectedItems.map(({ product, quantity }) => <div className="review-line" key={product.id}><span>{product.name} × {quantity}</span><strong>{yen(product.price_yen * quantity)}</strong></div>)}<div className="review-total"><span>お支払い予定</span><strong>{yen(total)}</strong></div></ReviewBlock>
      <ReviewBlock title="お客様情報" onEdit={() => setStep(2)}><strong>{name} 様</strong><span>{phone}</span>{note ? <p>{note}</p> : null}</ReviewBlock>
      <Notice>お支払いは商品受け取り時に店頭でお願いいたします。</Notice>
    </section> : null}

    <footer className="order-actionbar">
      {step > 0 ? <button className="back-button" type="button" onClick={() => { setStep((current) => current - 1); setError(""); }}><Icon name="back"/>戻る</button> : <span/>}
      <div className="action-summary">{step === 1 || step === 3 ? <span><strong>{itemCount}点</strong><small>{yen(total)}</small></span> : null}
        {error ? <span className="action-error" role="alert">{error}</span> : null}
        {duplicateWarning ? <span className="duplicate-warning" role="alert">{duplicateWarning}<Button onClick={() => void submit(true)} disabled={submitting}>重複でも予約する</Button></span> : null}
        {step < 3 ? <Button onClick={next} disabled={(step === 0 && !windowId) || (step === 1 && itemCount === 0)}>次へ</Button> : <Button onClick={() => void submit(false)} disabled={submitting || Boolean(duplicateWarning)}>{submitting ? "送信中…" : "この内容で予約する"}</Button>}
      </div>
    </footer>
  </main>;
}

function ReviewBlock({ title, onEdit, children }: { title: string; onEdit: () => void; children: React.ReactNode }) {
  return <section className="review-block"><header><h2>{title}</h2><button type="button" onClick={onEdit}>変更</button></header><div className="review-block__body">{children}</div></section>;
}
